import { useState, useEffect } from 'react';
import { apiJsonFetch } from '../utils/api';
import {
  Plus,
  Zap,
  Play,
  Pause,
  Trash2,
  Pencil,
  FlaskConical,
  X,
  AlertCircle,
} from 'lucide-react';

interface AutomationRule {
  id: string;
  name: string;
  description?: string;
  triggerType: string;
  triggerConditions?: object;
  actions: Array<{ type: string; config: object }>;
  isEnabled: boolean;
  priority: number;
  lastExecutedAt?: string;
  executionCount: number;
}

const triggerTypes = [
  { value: 'order_received', label: '受注時' },
  { value: 'payment_completed', label: '決済完了時' },
  { value: 'status_changed', label: 'ステータス変更時' },
  { value: 'inventory_low', label: '在庫不足時' },
];

const actionTypes = [
  { value: 'send_email', label: 'メール送信' },
  { value: 'update_status', label: 'ステータス更新' },
  { value: 'create_shipment', label: '出荷指示作成' },
  { value: 'notify', label: '通知送信' },
];

export default function AutomationPage() {
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      setLoading(true);
      const res = await apiJsonFetch('/api/automation/rules');
      if (res.ok) {
        setRules(res.data.rules || []);
      }
    } catch (error) {
      console.error('ルール取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleToggleRule = async (rule: AutomationRule) => {
    try {
      await apiJsonFetch(`/api/automation/rules/${rule.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ enabled: !rule.isEnabled }),
      });
      fetchRules();
    } catch (error) {
      console.error('ルール更新エラー:', error);
    }
  };

  const handleTestRule = async (ruleId: string) => {
    try {
      const res = await apiJsonFetch(`/api/automation/rules/${ruleId}/test`, {
        method: 'POST',
      });
      
      if (res.ok && res.data.results) {
        const results = res.data.results as Array<{ action: string; success: boolean; message: string }>;
        const resultText = results.map(r => 
          `${r.success ? '✓' : '✗'} ${r.action}: ${r.message}`
        ).join('\n');
        
        alert(`テスト実行結果:\n\n${resultText}\n\n${res.data.message}`);
        fetchRules(); // 実行回数を更新
      } else {
        alert(`テスト実行に失敗しました\n${res.data?.error || res.data?.message || '不明なエラー'}`);
      }
    } catch (error: any) {
      console.error('テスト実行エラー:', error);
      alert(`テスト実行に失敗しました: ${error.message || '不明なエラー'}`);
    }
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (!confirm('このルールを削除しますか？')) return;
    try {
      await apiJsonFetch(`/api/automation/rules/${ruleId}`, {
        method: 'DELETE',
      });
      fetchRules();
    } catch (error) {
      console.error('削除エラー:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">受注処理自動化</h1>
          <p className="text-sm text-gray-500 mt-1">ワークフロー・自動化ルールの設定</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus className="h-5 w-5" />
          ルール追加
        </button>
      </div>

      {/* ルール一覧 */}
      {loading ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="text-gray-500 mt-2">読み込み中...</p>
        </div>
      ) : rules.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <Zap className="h-12 w-12 text-gray-300 mx-auto" />
          <p className="text-gray-500 mt-2">自動化ルールが設定されていません</p>
          <button
            onClick={() => setShowAddModal(true)}
            className="mt-4 text-blue-600 hover:text-blue-800 font-medium"
          >
            ルールを追加する
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {rules.map((rule) => (
            <div
              key={rule.id}
              className={`bg-white rounded-xl shadow-sm border border-gray-200 p-6 ${
                !rule.isEnabled ? 'opacity-60' : ''
              }`}
            >
              <div className="flex items-start justify-between">
                {/* ルール情報 */}
                <div className="flex items-start gap-4">
                  <div
                    className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      rule.isEnabled ? 'bg-blue-100' : 'bg-gray-100'
                    }`}
                  >
                    <Zap
                      className={`h-6 w-6 ${
                        rule.isEnabled ? 'text-blue-600' : 'text-gray-400'
                      }`}
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900">{rule.name}</h3>
                    {rule.description && (
                      <p className="text-sm text-gray-500 mt-1">{rule.description}</p>
                    )}
                    <div className="flex flex-wrap gap-2 mt-3">
                      {/* トリガー */}
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                        トリガー:{' '}
                        {triggerTypes.find((t) => t.value === rule.triggerType)?.label ||
                          rule.triggerType}
                      </span>
                      {/* アクション */}
                      {rule.actions.map((action, idx) => (
                        <span
                          key={idx}
                          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"
                        >
                          {actionTypes.find((a) => a.value === action.type)?.label ||
                            action.type}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* 実行情報・アクション */}
                <div className="flex items-center gap-4">
                  <div className="text-right text-sm">
                    <p className="text-gray-500">実行回数</p>
                    <p className="font-medium text-gray-900">{rule.executionCount}回</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleToggleRule(rule)}
                      className={`p-2 rounded-lg transition-colors ${
                        rule.isEnabled
                          ? 'text-green-600 bg-green-50 hover:bg-green-100'
                          : 'text-gray-400 bg-gray-50 hover:bg-gray-100'
                      }`}
                      title={rule.isEnabled ? '無効にする' : '有効にする'}
                    >
                      {rule.isEnabled ? (
                        <Pause className="h-5 w-5" />
                      ) : (
                        <Play className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleTestRule(rule.id)}
                      className="p-2 text-blue-400 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                      title="テスト実行"
                    >
                      <FlaskConical className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => setEditingRule(rule)}
                      className="p-2 text-gray-400 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      title="編集"
                    >
                      <Pencil className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleDeleteRule(rule.id)}
                      className="p-2 text-red-400 bg-red-50 rounded-lg hover:bg-red-100 transition-colors"
                      title="削除"
                    >
                      <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* プリセットルール */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h2 className="font-semibold text-gray-900 mb-4">おすすめの自動化ルール</h2>
        <div className="grid gap-4 md:grid-cols-2">
          {[
            {
              name: '受注確認メール自動送信',
              trigger: 'order_received',
              triggerLabel: '受注時',
              action: 'send_email',
              actionLabel: 'メール送信',
              description: '受注時に自動で確認メールを送信',
            },
            {
              name: '決済完了で出荷指示作成',
              trigger: 'payment_completed',
              triggerLabel: '決済完了時',
              action: 'create_shipment',
              actionLabel: '出荷指示作成',
              description: '決済完了時に自動で出荷指示を作成',
            },
            {
              name: '在庫不足アラート',
              trigger: 'inventory_low',
              triggerLabel: '在庫不足時',
              action: 'notify',
              actionLabel: '通知送信',
              description: '在庫が閾値を下回ったら管理者に通知',
            },
            {
              name: '出荷完了メール自動送信',
              trigger: 'status_changed',
              triggerLabel: 'ステータス変更時',
              action: 'send_email',
              actionLabel: 'メール送信',
              description: '出荷完了時に発送通知メールを送信',
            },
          ].map((preset, idx) => (
            <div
              key={idx}
              onClick={() => setEditingRule({
                id: '',
                name: preset.name,
                description: preset.description,
                triggerType: preset.trigger,
                actions: [{ type: preset.action, config: {} }],
                isEnabled: true,
                priority: 0,
                executionCount: 0,
              })}
              className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 hover:bg-blue-50/50 transition-colors cursor-pointer"
            >
              <h3 className="font-medium text-gray-900">{preset.name}</h3>
              <p className="text-sm text-gray-500 mt-1">{preset.description}</p>
              <div className="flex gap-2 mt-3">
                <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                  {preset.triggerLabel}
                </span>
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                  {preset.actionLabel}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ルール追加モーダル */}
      {showAddModal && (
        <AddRuleModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            setShowAddModal(false);
            fetchRules();
          }}
        />
      )}

      {/* ルール編集モーダル */}
      {editingRule && (
        <EditRuleModal
          rule={editingRule}
          isNew={!editingRule.id}
          onClose={() => setEditingRule(null)}
          onSuccess={() => {
            setEditingRule(null);
            fetchRules();
          }}
        />
      )}
    </div>
  );
}

// ルール追加モーダル
function AddRuleModal({ onClose, onSuccess }: { onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    triggerType: 'order_received',
    actionType: 'send_email',
    priority: 0,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const res = await apiJsonFetch('/api/automation/rules', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          description: formData.description,
          trigger: {
            type: formData.triggerType,
            conditions: {},
          },
          actions: [{ type: formData.actionType, config: {} }],
          priority: formData.priority,
        }),
      });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">自動化ルール追加</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ルール名 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">トリガー *</label>
              <select
                value={formData.triggerType}
                onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {triggerTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">アクション *</label>
              <select
                value={formData.actionType}
                onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {actionTypes.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">優先度</label>
              <input
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                キャンセル
              </button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? '追加中...' : '追加'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// ルール編集モーダル
function EditRuleModal({ rule, isNew, onClose, onSuccess }: { rule: AutomationRule; isNew: boolean; onClose: () => void; onSuccess: () => void }) {
  const [formData, setFormData] = useState({
    name: rule.name,
    description: rule.description || '',
    triggerType: rule.triggerType,
    actionType: rule.actions[0]?.type || 'send_email',
    priority: rule.priority,
    enabled: rule.isEnabled,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        name: formData.name,
        description: formData.description,
        trigger: {
          type: formData.triggerType,
          conditions: {},
        },
        actions: [{ type: formData.actionType, config: {} }],
        priority: formData.priority,
        enabled: formData.enabled,
      };

      const res = isNew
        ? await apiJsonFetch('/api/automation/rules', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          })
        : await apiJsonFetch(`/api/automation/rules/${rule.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          });

      if (!res.ok) throw new Error(res.data?.error || 'エラーが発生しました');
      onSuccess();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'エラーが発生しました');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="w-full max-w-lg bg-white rounded-xl shadow-xl">
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">{isNew ? 'ルール作成' : 'ルール編集'}</h2>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
              <AlertCircle className="h-4 w-4" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ルール名 *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">説明</label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">トリガー *</label>
              <select
                value={formData.triggerType}
                onChange={(e) => setFormData({ ...formData, triggerType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {triggerTypes.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">アクション *</label>
              <select
                value={formData.actionType}
                onChange={(e) => setFormData({ ...formData, actionType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {actionTypes.map((a) => (
                  <option key={a.value} value={a.value}>{a.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">優先度</label>
              <input
                type="number"
                min="0"
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) || 0 })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {!isNew && (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={formData.enabled}
                  onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                  className="rounded"
                />
                <label htmlFor="enabled" className="text-sm text-gray-700">有効にする</label>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <button type="button" onClick={onClose} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                キャンセル
              </button>
              <button type="submit" disabled={loading} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {loading ? '保存中...' : isNew ? '作成' : '更新'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

